import { Component, OnInit, Input, EventEmitter, ComponentRef } from "@angular/core";
import { MatSlideToggleChange } from "@angular/material";

import { GenericTable } from "../../../genericTable/models";
import { GatewayView } from "../gateway-view";
import { GatewaysTableBaseComponent } from "../gateways-table-base.component";
import { StatusComponent } from "./status/status.component";

@Component({
	selector: "sdp-gateway-joined",
	templateUrl: "./gateway-joined.component.html",
	styleUrls: ["./_gateway-joined-css.scss"]
})
export class GatewayJoinedComponent extends GatewaysTableBaseComponent implements OnInit {
	private _gatewaysData: GatewayView[] = [];
	private _activeGateways: GatewayView[] = [];
	private _inactiveGateways: GatewayView[] = [];

	@Input()
	public disabled = false;

	public gatewayTypeSelect: EventEmitter<string> = new EventEmitter();

	public columnSchema: GenericTable.ColumnSchema[] = [];

	@Input()
	public get gatewaysData(): GatewayView[] {
		return this._gatewaysData;
	}

	public set gatewaysData(value: GatewayView[]) {
		this._gatewaysData = value;
		this._activeGateways = [];
		this._inactiveGateways = [];
		for (const gateway of this._gatewaysData.sort((a, b) => a.label.localeCompare(b.label))) {
			if (gateway.state === "active") {
				this._activeGateways.push(gateway);
			} else {
				this._inactiveGateways.push(gateway);
			}
		}
	}

	public get activeGateways() {
		return this._activeGateways;
	}

	public get inactiveGateways() {
		return this._inactiveGateways;
	}

	public ngOnInit() {
		this.columnSchema = this.getGatewaySchema();
	}

	public async activatePrivateGateway(row: GatewayView, event: MatSlideToggleChange) {
		try {
			// by the time this is invoked we must have some data to have populated the UI, so the ! is OK
			const activeCount = this.gatewaysData!.filter((g) => g.state === "active").length;
			// when we calculate the number that are active we'll have already toggled the state of the gateway in memory, so we are
			// looking to see if there are now zero active gateways
			// this means we'll have deactivated the last active one
			if (activeCount === 0 && !event.checked) {
				// silently do nothing, this is the last active gateway and we don't want to allow you to deactivate it without activating
				// cloud at the same time
				return false;
			}

			const result = await this.uiService.setActiveGateway(row, event.checked);
			if (result) {
				this.snackbarService.open(this.translateService.translate(
					event.checked ? "sdp.gateway.activated" : "sdp.gateway.deactivated",
					{
						label: row.label
					}
				));
			}
			return result;
		} catch (e) {
			this.snackbarService.showErrorBar(e, { label: row.label });
			throw e;
		}
	}

	public getGatewaySchema() {
		const columnSchema = this.getBaseGatewaySchema();
		columnSchema.unshift({
			minWidth: "5%",
			type: "button",
			headerSchema: {
				type: "none"
			},
			bodySchema: {
				type: "button",
				buttons: [
					// when the gateway is active you can deactivate it
					{
						type: "text",
						text: "sdp.label.deactivate",
						showButton: (row: GatewayView) => {
							return row.state === "active";
						},
						tooltip: () => {
							// if there is only one active gateway the button is disabled
							// a tooltip explains why
							if (this.activeGateways.length === 1) {
								return "privategateway.deactivate.disabled.tooltip";
							}
						},
						onClick: (row: GatewayView) => {
							this.deactivateGateway(row)
								.catch((e) => {
									this.snackbarService.showErrorBar(e);
								});
						},
						disabled: () => {
							// if there is only one active gateway the button is disabled
							return this.activeGateways.length === 1;
						}
					},
					// when the gateway is inactive you can activate it
					{
						type: "text",
						text: "sdp.label.activate",
						showButton: (row: GatewayView) => {
							return row.state !== "active";
						},
						onClick: (row: GatewayView) => {
							this.activateGateway(row)
								.catch((e) => {
									this.snackbarService.showErrorBar(e);
								});
						}
					}
				]
			}
		});
		columnSchema.splice(
			columnSchema.length - 1,
			0,
			{
				type: "customComponent",
				minWidth: "18%",
				headerSchema: {
					type: "basic",
					sortable: false,
					headerText: "sdp.label.status"
				},
				bodySchema: {
					type: "customComponent",
					customComponent: StatusComponent,
					callback: (componentRef: ComponentRef<StatusComponent>, gateway: GatewayView) => {
						componentRef.instance.gateway = gateway;
					}
				}
			}
		);
		return columnSchema;
	}

	private async activateGateway(gateway: GatewayView) {
		const hosts = this.service.getPublicHostAndPorts(gateway.hostAndPorts);
		// SDP-465 Removed overlapping check to relax the error
		const result = await this.uiService.setActiveGateway(gateway, true);
		if (result) {
			this.snackbarService.open(this.translateService.translate(
				"sdp.gateway.activated",
				{
					label: gateway.label
				}
			));
		}
	}

	private async deactivateGateway(gateway: GatewayView) {
		const result = await this.uiService.setActiveGateway(gateway, false);
		if (result) {
			this.snackbarService.open(this.translateService.translate(
				"sdp.gateway.deactivated",
				{
					label: gateway.label
				}
			));
		}
	}
}
